import getApiBase from './apiBase';

const VISITOR_KEY = 'ych.analytics.visitor';
const SESSION_KEY = 'ych.analytics.session';
const SESSION_TIMEOUT = 30 * 60 * 1000;
const MAX_QUEUE = 100;
const MAX_BATCH = 20;
const MAX_BODY = 16 * 1024;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const TARGET = /^[A-Za-z][A-Za-z0-9_.:-]{0,95}$/;
const PRODUCT = /^[A-Za-z0-9_-]{1,96}$/;
const LOCALES = new Set(['en', 'ja', 'zh-CN', 'zh-TW', 'fr', 'es', 'ko']);
const EVENTS = new Set([
  'page_view', 'click', 'add_to_cart', 'remove_from_cart', 'checkout_start',
  'checkout_redirect', 'login', 'register', 'custom_request_submit', 'contact_submit',
]);
const PUBLIC_PATHS = new Set([
  '/', '/team', '/store', '/cart', '/customizer', '/custom-design', '/stories',
  '/checkout', '/contact', '/shipping', '/login', '/register', '/community',
  '/community/scoring', '/guides/blitzball-vs-windball',
]);

let ready = false;
let authChanging = false;
let page = null;
let previousPage = null;
let navigationKey = null;
let locale = 'en';
let visitorId;
let session;
let queue = [];
const pending = new Set();

const privacyEnabled = () => typeof window !== 'undefined'
  && navigator.doNotTrack !== '1'
  && navigator.doNotTrack !== 'yes'
  && window.doNotTrack !== '1'
  && navigator.globalPrivacyControl !== true;

const readStorage = (type, key) => {
  try { return window[type].getItem(key); } catch { return null; }
};
const writeStorage = (type, key, value) => {
  try { window[type].setItem(key, value); } catch { /* Storage is optional. */ }
};

const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const sanitizeAnalyticsPath = (value) => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  const path = value.split(/[?#]/)[0].replace(/\/$/, '') || '/';
  if (PUBLIC_PATHS.has(path)) return path;
  if (/^\/store\/[A-Za-z0-9_-]{1,96}$/.test(path)) return path;
  if (/^\/community\/articles\/[^/]+$/.test(path)) return '/community/articles/:articleId';
  if (/^\/community\/scoring\/[^/]+$/.test(path)) return '/community/scoring/:videoId';
  return null;
};

const ensureIdentity = () => {
  if (!visitorId) {
    const storedVisitor = readStorage('localStorage', VISITOR_KEY);
    visitorId = UUID.test(storedVisitor || '') ? storedVisitor : createId();
    writeStorage('localStorage', VISITOR_KEY, visitorId);
  }
  if (!session) {
    try { session = JSON.parse(readStorage('sessionStorage', SESSION_KEY)); } catch { session = null; }
  }
  const now = Date.now();
  if (!UUID.test(session?.id || '') || !Number.isFinite(session?.lastActivity)
    || now - session.lastActivity >= SESSION_TIMEOUT || session.lastActivity > now
    || session.order >= 1000000000) {
    if (queue.length) flushAnalytics();
    session = { id: createId(), lastActivity: now, order: 0 };
  } else {
    session.lastActivity = now;
  }
  session.order = Number.isInteger(session.order) && session.order >= 0 ? session.order + 1 : 1;
  writeStorage('sessionStorage', SESSION_KEY, JSON.stringify(session));
};

const sendBatch = (body, keepalive) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  let timer;
  const timeout = new Promise((resolve) => {
    timer = window.setTimeout(() => { controller?.abort(); resolve(); }, 5000);
  });
  // Every batch is best effort. Failed batches are discarded, so an auth change
  // cannot later attribute an old visitor's actions to a newly logged-in user.
  let request;
  try {
    const transport = Promise.resolve(fetch(`${getApiBase()}/api/analytics/events`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive,
      ...(controller ? { signal: controller.signal } : {}),
    })).catch(() => {});
    request = Promise.race([transport, timeout]).finally(() => {
      window.clearTimeout(timer);
      pending.delete(request);
    });
  } catch {
    window.clearTimeout(timer);
    return Promise.resolve();
  }
  pending.add(request);
  return request;
};

export const flushAnalytics = ({ keepalive = false } = {}) => {
  if (!privacyEnabled()) {
    queue = [];
    return Promise.resolve();
  }
  const waiting = Array.from(pending);
  while (queue.length && visitorId && session) {
    const events = [];
    let body;
    while (queue.length && events.length < MAX_BATCH) {
      const next = JSON.stringify({ visitorId, sessionId: session.id, events: [...events, queue[0]] });
      if (next.length > MAX_BODY) break;
      events.push(queue.shift());
      body = next;
    }
    if (!events.length) { queue.shift(); continue; }
    waiting.push(sendBatch(body, keepalive));
  }
  return Promise.all(waiting).then(() => {});
};

export const setAnalyticsReady = (value) => { ready = Boolean(value); };

export const trackAnalyticsEvent = (name, details = {}) => {
  if (!ready || authChanging || !page || !privacyEnabled() || !EVENTS.has(name)) return;
  ensureIdentity();
  const event = { id: createId(), name, page, locale, sessionOrder: session.order };
  if (previousPage) event.previousPage = previousPage;
  if (TARGET.test(details.target || '')) event.target = details.target;
  if (name === 'click' && !event.target) return;
  const targetPath = sanitizeAnalyticsPath(details.targetPath);
  if (targetPath) event.targetPath = targetPath;
  const productId = String(details.productId ?? '');
  if (PRODUCT.test(productId)) event.productId = productId;
  if (Number.isInteger(details.quantity) && details.quantity > 0 && details.quantity <= 9999) {
    event.quantity = details.quantity;
  }
  queue.push(event);
  if (queue.length > MAX_QUEUE) queue.shift();
};

export const trackAnalyticsPage = (pathname, nextLocale, nextNavigationKey = pathname) => {
  locale = LOCALES.has(nextLocale) ? nextLocale : 'en';
  const nextPage = sanitizeAnalyticsPath(pathname);
  if (!ready || authChanging || (nextPage === page && nextNavigationKey === navigationKey)) return;
  flushAnalytics();
  previousPage = nextPage ? page : null;
  page = nextPage;
  navigationKey = nextNavigationKey;
  if (page) trackAnalyticsEvent('page_view');
};

export const beginAnalyticsAuthChange = async () => {
  authChanging = true;
  let timer;
  await Promise.race([
    flushAnalytics(),
    new Promise((resolve) => { timer = window.setTimeout(resolve, 1000); }),
  ]);
  window.clearTimeout(timer);
};
export const endAnalyticsAuthChange = () => { authChanging = false; };

const captureClick = (event) => {
  if (!ready || authChanging || !page || !privacyEnabled()) return;
  if (event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
  const element = event.target instanceof Element
    ? event.target.closest('button, a[href], [role="button"]') : null;
  if (!element || element.matches(':disabled, [aria-disabled="true"]')
    || element.closest('[data-analytics-ignore]')) return;
  const explicit = element.getAttribute('data-analytics-target');
  const indices = [];
  let node = element;
  while (node && node !== document.body && indices.length < 8) {
    const siblings = node.parentElement ? Array.from(node.parentElement.children) : [];
    indices.unshift(siblings.indexOf(node) + 1);
    node = node.parentElement;
  }
  const target = TARGET.test(explicit || '') ? explicit
    : `${element.tagName.toLowerCase()}:${indices.join('.')}`;
  let targetPath;
  if (element.tagName === 'A') {
    try {
      const url = new URL(element.getAttribute('href'), window.location.href);
      if (url.origin === window.location.origin) targetPath = sanitizeAnalyticsPath(url.pathname);
    } catch { /* Invalid or external links carry no destination data. */ }
  }
  trackAnalyticsEvent('click', { target, targetPath });
};

export const startAnalyticsCollector = () => {
  const onHidden = () => {
    if (document.visibilityState === 'hidden') flushAnalytics({ keepalive: true });
  };
  const onPageHide = () => { flushAnalytics({ keepalive: true }); };
  document.addEventListener('click', captureClick, true);
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', onPageHide);
  const interval = window.setInterval(flushAnalytics, 10000);
  return () => {
    document.removeEventListener('click', captureClick, true);
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', onPageHide);
    window.clearInterval(interval);
  };
};
